import { Navigate, useParams } from 'react-router-dom';
import type { Lang } from '../../lib/i18n';
import { matchupStrings, fmt, toolPath } from '../../lib/tools';
import { POPULAR_PAIRS, POPULAR_PAIR_SLUGS, PROFILE_BY_SLUG, resolvePair } from '../../lib/matchupData';
import { ToolLanding } from '../../components/tools/ToolLanding';
import { Comparison, MATCHUP_SLUG, Matchup, PopularMatchups } from '../../components/tools/Matchup';

/**
 * /tools/armwrestling-matchup — and, with a :pair param, one of the pre-rendered
 * popular matchup pages (/tools/armwrestling-matchup/<a>-vs-<b>), which get their
 * own title, H1 and canonical.
 */
export default function MatchupPage({ lang = 'en' as Lang }: { lang?: Lang }) {
  const { pair } = useParams();
  const t = matchupStrings(lang);
  const resolved = pair ? resolvePair(pair) : null;
  if (pair && (!resolved || !POPULAR_PAIR_SLUGS.includes(pair))) return <Navigate to={toolPath(lang, MATCHUP_SLUG)} replace />;

  const names = resolved ? { a: resolved[0].name, b: resolved[1].name } : null;
  const [ea, eb] = POPULAR_PAIRS[1] ?? POPULAR_PAIRS[0];
  const exA = PROFILE_BY_SLUG.get(ea)!;
  const exB = PROFILE_BY_SLUG.get(eb)!;

  return (
    <ToolLanding
      lang={lang}
      slug={MATCHUP_SLUG}
      copy={t}
      embedHeight={1300}
      overrides={
        names && pair
          ? {
              title: fmt(t.pair.metaTitle, names),
              description: fmt(t.pair.metaDescription, names),
              h1: fmt(t.pair.h1, names),
              intro: fmt(t.pair.intro, names),
              path: `/tools/${MATCHUP_SLUG}/${pair}`,
              alternatesPath: `/tools/${MATCHUP_SLUG}/${pair}`,
            }
          : undefined
      }
      tool={<Matchup lang={lang} initial={resolved ? [resolved[0].slug, resolved[1].slug] : undefined} />}
      extra={<PopularMatchups lang={lang} current={pair} />}
      example={
        resolved ? undefined : (
          <section className="mb-14" aria-labelledby="example-heading">
            <h2 id="example-heading" className="text-2xl md:text-3xl font-extrabold text-ink mb-2">{t.example.heading}</h2>
            <p className="text-muted mb-5">{t.example.intro}</p>
            <Comparison a={exA} b={exB} lang={lang} headingLevel="h3" />
          </section>
        )
      }
    />
  );
}
